import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { closeLead, saveLeadCloseReason, viewLeadClosureReasons } from 'services/lead/leadServices';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { useToggle } from 'hooks/useToggle';
import { createLeadReferral, listLeadReferrals } from 'services/leadServices';

export function useCloseLead(leadDetails, onHide, practice_area = '', is_referral_entity = false) {
  const {
    handleSubmit,
    control,
    formState: { errors },
    register,
    watch,
    setValue,
    trigger,
    clearErrors,
    getValues,
  } = useForm();
  const { addToast } = useToast();
  const [reasonList, setReasonList] = useState([]);
  const [referralsList, setReferralsList] = useState([]);
  const [showCustomReason, setShowCustomReason] = useToggle();
  const [showReferredTo, setShowReferredTo] = useToggle();
  const [showCustomReferral, setShowCustomReferral] = useToggle();
  const [showCustomReferralReason, setShowCustomReferralReason] = useToggle();
  const [loading, setLoading] = useToggle();
  const [saveLoading, setSaveLoading] = useToggle();

  useEffect(() => {
    loadLeadReferrals();
  }, []);

  const practiceArea = practice_area || leadDetails?.case_description?.case_practice_area;

  useEffect(() => {
    loadCloseReasons();
    loadLeadReferrals();
  }, [practiceArea]);

  const loadCloseReasons = () => {
    setLoading(true);
    viewLeadClosureReasons(true, false, 'alphabet_ascending', 1000, '', 'forward', practiceArea)
      .then((response) => {
        setReasonList(response.data?.['closure-reason'] ?? []);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const createCloseLeadReason = (reasonText) => {
    setLoading(true);
    return saveLeadCloseReason(leadDetails?.case_id, { reason: reasonText, practice_area_id: 'ALL' }, true)
      .then((response) => {
        addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.LEAD_CLOSURE_REASON_CREATED);
        loadCloseReasons();

        // Use the actual structure from the API response
        const newReason = {
          closure_reason: response?.data?.closure_reason,
          sk: response?.data?.sk,
          is_lead: response?.data?.is_lead,
          pk: response?.data?.pk,
          is_default: response?.data?.is_default ? response?.data?.is_default : false,
        };
        setValue('old_reason', newReason);

        setShowCustomReason();

        // Return the new reason for auto-population
        return newReason;
      })
      .catch((error) => {
        console.log(error);
        addToast(
          false,
          toastConstant.toasterType.ERROR,
          toastConstant.api.FAILED,
          toastConstant.message.LEAD_CLOSURE_REASON_CREATED_FAILED
        );
        return null;
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const addCloseLeadReason = (formData) => {
    setSaveLoading(true);
    const leadCloseReason = formData?.old_reason; // Only use old_reason since new_reason is already created

    const dataToSent = {
      is_open: false,
      previous_lead_stage: leadDetails?.lead_stage,
      lead_close_reason: leadCloseReason?.closure_reason,
      is_qualified: formData?.is_qualified,
      referred_to: leadCloseReason?.closure_reason === 'Referred Out' ? formData?.old_referred_to || formData?.referred_to : null,
      referred_out_reason_id: leadCloseReason?.closure_reason === 'Referred Out' ? formData?.referral_reason : null,
    };

    const saveReferralPromise =
      leadCloseReason?.closure_reason === 'Referred Out' && formData?.referred_to
        ? createLeadReferral(true, { referred_to: formData?.referred_to })
        : Promise.resolve();

    const promiseList = [saveReferralPromise];
    Promise.all(promiseList)
      .then(() => handleCloseLead(dataToSent))
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setSaveLoading(false);
      });
  };

  const loadLeadReferrals = () => {
    setLoading(true);
    listLeadReferrals(is_referral_entity)
      .then((response) => {
        setReferralsList(response.data ?? []);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleCloseLead = (dataToSent) => {
    setSaveLoading(true);
    closeLead(leadDetails?.sk, dataToSent)
      .then(() => {
        addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.LEAD_CLOSE_SUCCESS);
        onHide('closed');
      })
      .catch(() => {
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.LEAD_CLOSE_FAILURE);
      })
      .finally(() => {
        setSaveLoading(false);
      });
  };

  return {
    handleSubmit,
    control,
    register,
    watch,
    setValue,
    trigger,
    clearErrors,
    getValues,
    reasonList,
    setReasonList,
    showCustomReason,
    loading,
    saveLoading,
    addCloseLeadReason,
    createCloseLeadReason,
    handleCloseLead,
    errors,
    setShowCustomReason,
    showReferredTo,
    setShowReferredTo,
    showCustomReferral,
    setShowCustomReferral,
    showCustomReferralReason,
    setShowCustomReferralReason,
    referralsList,
  };
}
